import { useState, useMemo } from "react";
import { useGenerateRiskSummaryV3 } from "@/lib/api/riskV3";
import type { TaggedRiskRow } from "@/lib/api/riskV3";
import { applyFilterSpec } from "@/lib/risk/filters";
import type { FilterNode, RiskFilterSpec, RowFilterField, FilterOperator } from "@/lib/risk/types";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import { ComboboxMulti } from "@/components/ui/combobox_mutli";
import { useListCustomers } from "@/lib/api/customer";
import { useListTraders } from "@/lib/api/trader";
import { safeParseDecimal, formatNumberToDisplay } from "@/lib/api/common";
import { resolveFuzzyMatches } from "@/lib/risk/fuzzyMatch";
import { DataTable } from "@/components/ui/DataTable";
import type { DataTableColumn } from "@/components/ui/DataTable";
import { createColumnHelper } from "@tanstack/react-table";
import { V3ProfileCards } from "./V3ProfileCards";
import type { V3CustomerCommodityProfileItem } from "./V3ProfileCards";
import { V3TopLevelSummaryTable } from "./V3TopLevelSummaryTable";
import { DetailedCardsSection } from "./DetailedCards/DetailedCardsSection";

// ============================================================================
// Helpers
// ============================================================================

/** Walk a FilterNode tree and collect all customer_id values from conditions */
function extractCustomerIdsFromFilter(node: FilterNode): string[] {
  if (node.type === "condition") {
    if (node.field === "customer_id") {
      if (Array.isArray(node.value)) return node.value as string[];
      if (typeof node.value === "string" && node.value) return [node.value];
    }
    return [];
  }
  if (node.type === "not") {
    return extractCustomerIdsFromFilter(node.child);
  }
  // and / or
  const ids: string[] = [];
  for (const child of node.children) {
    ids.push(...extractCustomerIdsFromFilter(child));
  }
  return ids;
}

/** Walk a FilterNode tree and collect all commodity_group values from conditions */
function extractCommodityGroupsFromFilter(node: FilterNode): string[] {
  if (node.type === "condition") {
    if (node.field === "commodity_group") {
      if (Array.isArray(node.value)) return node.value as string[];
      if (typeof node.value === "string" && node.value) return [node.value];
    }
    return [];
  }
  if (node.type === "not") {
    return extractCommodityGroupsFromFilter(node.child);
  }
  const groups: string[] = [];
  for (const child of node.children) {
    groups.push(...extractCommodityGroupsFromFilter(child));
  }
  return groups;
}

/** Detail-level meta fields — when any of these are in the filter, profile cards should be hidden */
const DETAIL_META_FIELDS = new Set([
  "meta.is_cash", "meta.is_futures_only", "meta.is_basis_only", "meta.is_unpriced",
  "meta.is_open_offer", "meta.contributes_to_futures_hedging", "meta.contributes_to_physical_hedging",
  "meta.is_hta", "meta.is_otc", "meta.is_beacon", "meta.is_dp", "meta.is_dpq",
  "meta.is_direct_sale", "meta.has_double_up", "row_type",
]);

/** Check if any detail-level meta filter conditions exist in the filter tree */
function hasDetailMetaFilters(node: FilterNode): boolean {
  if (node.type === "condition") {
    return DETAIL_META_FIELDS.has(node.field);
  }
  if (node.type === "not") {
    return hasDetailMetaFilters(node.child);
  }
  return node.children.some((child) => hasDetailMetaFilters(child));
}

// ============================================================================
// Filter Builder Component
// ============================================================================

interface FilterBuilderProps {
  node: FilterNode;
  onChange: (node: FilterNode) => void;
  onRemove: () => void;
  depth: number;
  customers: Array<{ value: string; label: string }>;
  traders: Array<{ value: string; label: string }>;
  commodityGroups: Array<{ value: string; label: string }>;
}

function FilterBuilder({ node, onChange, onRemove, depth, customers, traders, commodityGroups }: FilterBuilderProps) {
  const indent = depth * 20;
  
  // AND/OR/NOT nodes
  if (node.type === "and" || node.type === "or") {
    return (
      <div style={{ marginLeft: indent }} className="border-l-2 border-gray-300 pl-2 mb-2">
        <div className="flex items-center gap-2 mb-2">
          <Select
            value={node.type}
            onValueChange={(value) => {
              onChange({ ...node, type: value as "and" | "or" });
            }}
          >
            <SelectTrigger className="w-20 h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="and">AND</SelectItem>
              <SelectItem value="or">OR</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => {
              onChange({
                ...node,
                children: [
                  ...node.children,
                  { type: "condition", field: "customer_id", operator: "eq", value: "" }
                ]
              });
            }}
          >
            + Condition
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => {
              onChange({
                ...node,
                children: [
                  ...node.children,
                  { type: "and", children: [] }
                ]
              });
            }}
          >
            + Group
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => {
              onChange({
                ...node,
                children: [
                  ...node.children,
                  { type: "not", child: { type: "condition", field: "customer_id", operator: "eq", value: "" } }
                ]
              });
            }}
          >
            + NOT
          </Button>
          
          {depth > 0 && (
            <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={onRemove}>
              Remove
            </Button>
          )}
        </div>
        
        {node.children.map((child, idx) => (
          <FilterBuilder
            key={idx}
            node={child}
            onChange={(updated) => {
              const newChildren = [...node.children];
              newChildren[idx] = updated;
              onChange({ ...node, children: newChildren });
            }}
            onRemove={() => {
              onChange({ ...node, children: node.children.filter((_, i) => i !== idx) });
            }}
            depth={depth + 1}
            customers={customers}
            traders={traders}
            commodityGroups={commodityGroups}
          />
        ))}
      </div>
    );
  }
  
  // NOT node
  if (node.type === "not") {
    return (
      <div style={{ marginLeft: indent }} className="border-l-2 border-red-300 pl-2 mb-2">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-bold text-red-600">NOT</span>
          <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={onRemove}>
            Remove
          </Button>
        </div>
        <FilterBuilder
          node={node.child}
          onChange={(updated) => onChange({ ...node, child: updated })}
          onRemove={() => {}}
          depth={depth + 1}
          customers={customers}
          traders={traders}
          commodityGroups={commodityGroups}
        />
      </div>
    );
  }
  
  // CONDITION node
  const condition = node;
  
  return (
    <div style={{ marginLeft: indent }} className="flex items-center gap-2 mb-2 flex-wrap">
      {/* Field Selector */}
      <Select
        value={condition.field}
        onValueChange={(value) => {
          onChange({ ...condition, field: value as RowFilterField });
        }}
      >
        <SelectTrigger className="w-48 h-7 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="customer_id">Customer</SelectItem>
          <SelectItem value="commodity_group">Commodity Group</SelectItem>
          <SelectItem value="row_type">Row Type</SelectItem>
          
          <SelectItem value="meta.is_cash" className="ml-4">→ Cash (Physical)</SelectItem>
          <SelectItem value="meta.is_futures_only" className="ml-4">→ Futures Only</SelectItem>
          <SelectItem value="meta.is_basis_only" className="ml-4">→ Basis Only</SelectItem>
          <SelectItem value="meta.is_unpriced" className="ml-4">→ Unpriced</SelectItem>
          <SelectItem value="meta.is_open_offer" className="ml-4">→ Open Offer</SelectItem>
          
          <SelectItem value="meta.is_hta" className="ml-4">→ HTA</SelectItem>
          <SelectItem value="meta.is_otc" className="ml-4">→ OTC</SelectItem>
          <SelectItem value="meta.is_beacon" className="ml-4">→ Beacon</SelectItem>
          <SelectItem value="meta.is_dp" className="ml-4">→ DP</SelectItem>
          <SelectItem value="meta.is_dpq" className="ml-4">→ DPQ</SelectItem>
          <SelectItem value="meta.is_direct_sale" className="ml-4">→ Direct Sale</SelectItem>
          <SelectItem value="meta.has_double_up" className="ml-4">→ Has Double Up</SelectItem>
          
          <SelectItem value="meta.contributes_to_futures_hedging" className="ml-4">→ Contributes to Futures</SelectItem>
          <SelectItem value="meta.contributes_to_physical_hedging" className="ml-4">→ Contributes to Physical</SelectItem>
          
          <SelectItem value="release_data.trader_id">Trader</SelectItem>
        </SelectContent>
      </Select>
      
      {/* Operator Selector */}
      <Select
        value={condition.operator}
        onValueChange={(value) => {
          onChange({ ...condition, operator: value as FilterOperator });
        }}
      >
        <SelectTrigger className="w-28 h-7 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="eq">equals</SelectItem>
          <SelectItem value="neq">not equals</SelectItem>
          <SelectItem value="in">in</SelectItem>
          <SelectItem value="not_in">not in</SelectItem>
          <SelectItem value="is_true">is true</SelectItem>
          <SelectItem value="is_false">is false</SelectItem>
          <SelectItem value="is_null">is null</SelectItem>
          <SelectItem value="is_not_null">is not null</SelectItem>
        </SelectContent>
      </Select>
      
      {/* Value Input */}
      {(condition.operator === "eq" || condition.operator === "neq") && (
        <>
          {condition.field === "customer_id" && (
            <Combobox
              virtualizeList
              popoverClassName="w-[400px]"
              options={customers}
              value={condition.value as string}
              onValueChange={(val: string) => onChange({ ...condition, value: val })}
              placeholder="Select customer..."
              className="w-48 h-7 text-xs"
              dwimOnChange
            />
          )}
          {condition.field === "commodity_group" && (
            <Combobox
              options={commodityGroups}
              value={condition.value as string}
              onValueChange={(val: string) => onChange({ ...condition, value: val })}
              placeholder="Select commodity..."
              className="w-48 h-7 text-xs"
              dwimOnChange
            />
          )}
          {condition.field === "row_type" && (
            <Select
              value={condition.value as string}
              onValueChange={(val) => onChange({ ...condition, value: val })}
            >
              <SelectTrigger className="w-32 h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="release">Release</SelectItem>
                <SelectItem value="position">Position</SelectItem>
              </SelectContent>
            </Select>
          )}
          {condition.field === "release_data.trader_id" && (
            <Combobox
              virtualizeList
              popoverClassName="w-[400px]"
              options={traders}
              value={condition.value as string}
              onValueChange={(val: string) => onChange({ ...condition, value: val })}
              placeholder="Select trader..."
              className="w-48 h-7 text-xs"
              dwimOnChange
            />
          )}
        </>
      )}
      
      {(condition.operator === "in" || condition.operator === "not_in") && (
        <>
          {condition.field === "customer_id" && (
            <ComboboxMulti
              options={customers}
              value={Array.isArray(condition.value) ? condition.value as string[] : []}
              onValueChange={(val: string[]) => onChange({ ...condition, value: val })}
              placeholder="Select customers..."
              className="w-48 h-7 text-xs"
              dwimOnChange
            />
          )}
          {condition.field === "commodity_group" && (
            <ComboboxMulti
              options={commodityGroups}
              value={Array.isArray(condition.value) ? condition.value as string[] : []}
              onValueChange={(val: string[]) => onChange({ ...condition, value: val })}
              placeholder="Select commodities..."
              className="w-48 h-7 text-xs"
              dwimOnChange
            />
          )}
        </>
      )}
      
      <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={onRemove}>
        Remove
      </Button>
    </div>
  );
}

// ============================================================================
// Table View Types & Columns
// ============================================================================

type TableView = "brokerage_positions" | "open_offers";

const columnHelper = createColumnHelper<TaggedRiskRow>();

function getBrokeragePositionColumns(): DataTableColumn<TaggedRiskRow>[] {
  return [
    columnHelper.accessor("meta.is_hedgeable", {
      id: "is_hedgeable",
      header: "Hedgeable",
    }),
    columnHelper.accessor("meta.commodity_display_name", {
      id: "commodity",
      header: "Commodity",
    }),
    columnHelper.accessor("meta.commodity_group", {
      id: "commodity_group",
      header: "Commodity Group",
    }),
    columnHelper.accessor((row) => row.position_data?.trading_account ?? "", {
      id: "trading_account",
      header: "Trading Account",
    }),
    columnHelper.accessor("customer_id", {
      id: "customer_id",
      header: "Customer ID",
    }),
    columnHelper.accessor((row) => row.position_data?.enabled ? "Open" : "Closed", {
      id: "enabled",
      header: "Status",
    }),
    columnHelper.accessor((row) => {
      const poc = row.position_data?.put_or_call;
      return poc === "P" ? "Put" : poc === "C" ? "Call" : "Future";
    }, {
      id: "put_or_call",
      header: "Put/Call",
    }),
    columnHelper.accessor("crop_year", {
      id: "crop_year",
      header: "Crop Year",
    }),
    columnHelper.accessor((row) => row.position_data?.option_month ?? "", {
      id: "option_month",
      header: "Option Month",
    }),
    columnHelper.accessor((row) => row.position_data?.long_qty ?? 0, {
      id: "long_qty",
      header: "Long Qty",
    }),
    columnHelper.accessor((row) => row.position_data?.short_qty ?? 0, {
      id: "short_qty",
      header: "Short Qty",
    }),
    columnHelper.accessor("quantity", {
      id: "quantity",
      header: "Quantity",
      cell: (info) => (
        <span className="text-gray-600 text-sm">
          {formatNumberToDisplay(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor((row) => row.position_data?.strike_price ?? "", {
      id: "strike_price",
      header: "Strike Price",
      cell: (info) => (
        <span className="text-gray-600 text-sm">
          {formatNumberToDisplay(info.getValue() as string)}
        </span>
      ),
    }),
    columnHelper.accessor("price", {
      id: "price",
      header: "Price",
      cell: (info) => (
        <span className="text-gray-600 text-sm">
          {formatNumberToDisplay(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor((row) => row.position_data?.premium ?? "", {
      id: "premium",
      header: "Premium",
      cell: (info) => (
        <span className="text-gray-600 text-sm">
          {formatNumberToDisplay(info.getValue() as string)}
        </span>
      ),
    }),
    columnHelper.accessor((row) => row.position_data?.contract_description ?? "", {
      id: "contract_description",
      header: "Contract",
    }),
    columnHelper.accessor("meta.brokerage_type", {
      id: "brokerage_type",
      header: "Brokerage Type",
    }),
    columnHelper.accessor("meta.brokerage_provider_type", {
      id: "brokerage_provider_type",
      header: "Provider",
    }),
  ] as DataTableColumn<TaggedRiskRow>[];
}

function getOpenOfferColumns(): DataTableColumn<TaggedRiskRow>[] {
  return [
    columnHelper.accessor("meta.is_hedgeable", {
      id: "is_hedgeable",
      header: "Hedgeable",
    }),
    columnHelper.accessor("meta.commodity_display_name", {
      id: "commodity",
      header: "Commodity",
    }),
    columnHelper.accessor("meta.commodity_group", {
      id: "commodity_group",
      header: "Commodity Group",
    }),
    columnHelper.accessor((row) => row.position_data?.trading_account ?? "", {
      id: "trading_account",
      header: "Trading Account",
    }),
    columnHelper.accessor("customer_id", {
      id: "customer_id",
      header: "Customer ID",
    }),
    columnHelper.accessor((row) => {
      const poc = row.position_data?.put_or_call;
      return poc === "P" ? "Put" : poc === "C" ? "Call" : "Future";
    }, {
      id: "put_or_call",
      header: "Put/Call",
    }),
    columnHelper.accessor("crop_year", {
      id: "crop_year",
      header: "Crop Year",
    }),
    columnHelper.accessor((row) => row.position_data?.option_month ?? "", {
      id: "option_month",
      header: "Option Month",
    }),
    columnHelper.accessor((row) => row.position_data?.long_qty ?? 0, {
      id: "long_qty",
      header: "Long Qty",
    }),
    columnHelper.accessor((row) => row.position_data?.short_qty ?? 0, {
      id: "short_qty",
      header: "Short Qty",
    }),
    columnHelper.accessor("quantity", {
      id: "quantity",
      header: "Quantity",
      cell: (info) => (
        <span className="text-gray-600 text-sm">
          {formatNumberToDisplay(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor((row) => row.position_data?.strike_price ?? "", {
      id: "strike_price",
      header: "Strike Price",
      cell: (info) => (
        <span className="text-gray-600 text-sm">
          {formatNumberToDisplay(info.getValue() as string)}
        </span>
      ),
    }),
    columnHelper.accessor("price", {
      id: "price",
      header: "Price",
      cell: (info) => (
        <span className="text-gray-600 text-sm">
          {formatNumberToDisplay(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor((row) => row.position_data?.premium ?? "", {
      id: "premium",
      header: "Premium",
      cell: (info) => (
        <span className="text-gray-600 text-sm">
          {formatNumberToDisplay(info.getValue() as string)}
        </span>
      ),
    }),
    columnHelper.accessor((row) => row.position_data?.contract_description ?? "", {
      id: "contract_description",
      header: "Contract",
    }),
    columnHelper.accessor("meta.brokerage_type", {
      id: "brokerage_type",
      header: "Brokerage Type",
    }),
    columnHelper.accessor("meta.brokerage_provider_type", {
      id: "brokerage_provider_type",
      header: "Provider",
    }),
  ] as DataTableColumn<TaggedRiskRow>[];
}

// ============================================================================
// Main Dashboard
// ============================================================================

export function RiskExperimentsSummaryDashboard() {
  const currentYear = new Date().getFullYear();
  
  // Crop year state
  const [selectedCropYear, setSelectedCropYear] = useState<number>(2025);
  
  const excludedCustomerIds = true ? [
    'be2a7e63-0be3-4e50-af6c-d9a25cdad79c', // lighthouse customer risk
    'c537951a-1afb-4172-b3a8-b566a20999a8', // Andrew
    '6f2cc3b6-8753-42b4-bd36-5ddfb5153128', // Pat
  ] : [];
  const { data, isLoading, isFetching, isError, error } = useGenerateRiskSummaryV3({
    crop_year: selectedCropYear,
    excluded_customer_ids: excludedCustomerIds,
  });
  
  // Filter state
  const [rootFilter, setRootFilter] = useState<FilterNode>({
    type: "and",
    children: []
  });
  
  // Table view state
  const [tableView, setTableView] = useState<TableView>("brokerage_positions");

  // Get data for dropdowns (used by both FilterBuilder and VoiceFilterRecorder)
  const minimalCustomerListResult = useListCustomers({ minimal: true, hasRiskProfile: true });
  const minimalTraderListResult = useListTraders();
  
  const customersRaw = (minimalCustomerListResult?.data?.data || []) as Array<{ id: string; long_name: string }>;
  const tradersRaw = (minimalTraderListResult?.data?.data || []) as Array<{ id: string; long_name: string }>;
  
  const customers = customersRaw.map((u) => ({
    value: u.id,
    label: u.long_name,
  }));
  const traders = tradersRaw.map((u) => ({
    value: u.id,
    label: u.long_name,
  }));

  const customerNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of customersRaw) {
      map[c.id] = c.long_name;
    }
    return map;
  }, [customersRaw]);

  // Extract customer IDs from the active filter tree (for profile cards)
  const filteredCustomerIds = useMemo(() => {
    return [...new Set(extractCustomerIdsFromFilter(rootFilter))];
  }, [rootFilter]);

  // Extract commodity groups and detect detail-level filters for profile card visibility
  const filteredCommodityGroups = useMemo(() => {
    return [...new Set(extractCommodityGroupsFromFilter(rootFilter))];
  }, [rootFilter]);

  const isDetailFiltered = useMemo(() => {
    return hasDetailMetaFilters(rootFilter);
  }, [rootFilter]);
  
  // TODO fix these, fix the backend to actually be the group
  const commodityGroups = [
    { value: "C", label: "Corn" },
    { value: "S", label: "Soybeans" },
    { value: "Wheat", label: "Wheat" },
    { value: "CNL", label: "Canola" },
    { value: "Oats", label: "Oats" },
    { value: "Barley", label: "Barley" },
    { value: "Cattle", label: "Cattle" },
  ];
  
  // Generate crop year options (current year and previous 5 years)
  const cropYearOptions = useMemo(() => {
    const years = [];
    for (let i = 0; i < 6; i++) {
      const year = currentYear - i;
      years.push({ value: String(year), label: String(year) });
    }
    return years;
  }, [currentYear]);
  
  // Apply filters
  const filteredData = useMemo(() => {
    if (!data?.data) return null;
    
    const spec: RiskFilterSpec = {
      crop_year: selectedCropYear,
      row_filter: rootFilter.type === "and" && rootFilter.children.length === 0 ? undefined : rootFilter,
    };
    // console.warn("spec", spec);
    // console.warn("spec", data.data.rows);
    
    return applyFilterSpec(data.data.rows, spec);
  }, [data, rootFilter, selectedCropYear]);

  // Define breakdown categories for extensibility
  type RiskRow = NonNullable<typeof filteredData>['rows'][number];
  
  type BreakdownCategory = {
    label: string;
    order: number; // For sorting within commodity
    categoryType: 'Futures' | 'Physical'; // For grouping
    filter: (row: RiskRow) => boolean;
  };

  const breakdownCategories: BreakdownCategory[] = ([
    {
      label: 'Futures HTA',
      categoryType: 'Futures',
      filter: (row) => row.meta.is_hta === true && row.meta.contributes_to_futures_hedging === true,
    },
    {
      label: 'Futures OTC',
      categoryType: 'Futures',
      filter: (row) => row.meta.is_otc === true && row.meta.contributes_to_futures_hedging === true,
    },
    {
      label: 'Futures Brokerage Positions',
      categoryType: 'Futures',
      filter: (row) => !!row.meta.brokerage_type && row.meta.is_hedgeable === true && row.meta.contributes_to_futures_hedging && row.meta.is_brokerage_position,
    },
    {
      label: 'Futures Brokerage Open Offers',
      categoryType: 'Futures',
      filter: (row) => !!row.meta.brokerage_type && row.meta.is_hedgeable === true && row.meta.is_open_offer,
    },
    {
      label: 'Futures Brokerage P&S',
      categoryType: 'Futures',
      filter: (row) => !!row.meta.brokerage_type && row.meta.is_hedgeable === true && row.meta.is_realized,
    },
    {
      label: 'Futures Direct Sales',
      order: 4,
      categoryType: 'Futures',
      filter: (row) => row.meta.is_direct_sale && row.meta.contributes_to_futures_hedging,
    },
    {
      label: 'Futures DPQ',
      categoryType: 'Physical',
      filter: (row) => row.meta.is_dpq && row.meta.contributes_to_futures_hedging,
    },
    {
      label: 'Physical Basis',
      categoryType: 'Physical',
      filter: (row) => row.meta.contributes_to_physical_hedging && row.meta.physical_contribution_type === 'basis' && row.meta.is_basis_only === true,
    },
    {
      label: 'Physical OTC',
      categoryType: 'Physical',
      filter: (row) => row.meta.is_otc === true && row.meta.is_basis_only === true,
    },
    {
      label: 'Physical Cash',
      categoryType: 'Physical',
      filter: (row) => row.meta.is_cash === true,
    },
    {
      label: 'Physical Direct Sales',
      categoryType: 'Physical',
      filter: (row) => row.meta.is_direct_sale && row.meta.contributes_to_physical_hedging,
    },
    {
      label: 'Physical DPQ',
      categoryType: 'Physical',
      filter: (row) => row.meta.is_dpq && row.meta.contributes_to_physical_hedging,
    },
    {
      label: 'DPQ Unset',
      categoryType: 'Physical',
      filter: (row) => (row.meta.is_dpq || row.meta.is_dp) && (!row.meta.contributes_to_futures_hedging && !row.meta.contributes_to_physical_hedging),
    },
  ] as Omit<BreakdownCategory, 'order'>[]).map((item, index) => ({ ...item, order: index +1 }));

  // Aggregate all breakdowns in a single pass through the data
  const summaryData = useMemo(() => {
    if (!filteredData?.rows) return null;

    // Structure: Map<commodity, Map<breakdownLabel, quantity>>
    const aggregates = new Map<string, Map<string, number>>();

    for (const row of filteredData.rows) {
      const quantity = safeParseDecimal({})(row.quantity ?? undefined).toNumber();
      const commodity = row.meta.commodity_group || "Unknown";

      if (!aggregates.has(commodity)) {
        aggregates.set(commodity, new Map());
      }
      const commodityMap = aggregates.get(commodity)!;

      // Check each breakdown category
      for (const category of breakdownCategories) {
        if (category.filter(row)) {
          commodityMap.set(
            category.label,
            (commodityMap.get(category.label) || 0) + quantity
          );
        }
      }
    }

    // Convert to sorted array structure for rendering
    type SummaryRow = {
      commodity: string;
      breakdown: string;
      categoryType: 'Futures' | 'Physical';
      order: number;
      quantity: number;
    };

    const rows: SummaryRow[] = [];
    
    for (const [commodity, breakdownMap] of aggregates.entries()) {
      for (const [breakdownLabel, quantity] of breakdownMap.entries()) {
        const category = breakdownCategories.find(c => c.label === breakdownLabel)!;
        rows.push({
          commodity,
          breakdown: breakdownLabel,
          categoryType: category.categoryType,
          order: category.order,
          quantity,
        });
      }
    }

    // Sort by commodity, then by category type (Futures before Physical), then by order
    rows.sort((a, b) => {
      if (a.commodity !== b.commodity) {
        return a.commodity.localeCompare(b.commodity);
      }
      if (a.categoryType !== b.categoryType) {
        return a.categoryType === 'Futures' ? -1 : 1;
      }
      return a.order - b.order;
    });

    // Calculate totals per breakdown
    const totals = new Map<string, number>();
    for (const row of rows) {
      totals.set(row.breakdown, (totals.get(row.breakdown) || 0) + row.quantity);
    }

    return { rows, totals };
  }, [filteredData]);

  // Table data: brokerage positions and open offers
  const brokeragePositionRows = useMemo(() => {
    if (!filteredData?.rows) return [];
    return filteredData.rows.filter((row) => row.meta.is_brokerage_position === true);
  }, [filteredData]);

  const openOfferRows = useMemo(() => {
    if (!filteredData?.rows) return [];
    return filteredData.rows.filter((row) => row.meta.is_open_offer === true);
  }, [filteredData]);

  const brokeragePositionColumns = useMemo(() => getBrokeragePositionColumns(), []);
  const openOfferColumns = useMemo(() => getOpenOfferColumns(), []);

  const activeTableData = tableView === "brokerage_positions" ? brokeragePositionRows : openOfferRows;
  const activeTableColumns = tableView === "brokerage_positions" ? brokeragePositionColumns : openOfferColumns;
  const activeTableLabel = tableView === "brokerage_positions" ? "Brokerage Positions" : "Open Offers";

  if (isLoading) return <div className="p-4">Loading...</div>;
  if (isError) return <div className="p-4 text-red-600">Error: {String(error)}</div>;
  if (!data?.data) return <div className="p-4">No data</div>;
  
  // Handle cell click from the summary table — builds a filter from the clicked row + column
  const handleSummaryTableCellClick = (row: V3CustomerCommodityProfileItem, columnId: string) => {
    const conditions: FilterNode[] = [];

    // Column groups that only need customer
    const customerOnlyColumns = new Set(["customer_id"]);

    // Column groups that need customer + commodity
    const customerCommodityColumns = new Set([
      "commodity_display_name", "acreage", "production", "projected_production",
      "actual_production", "projected_yield", "actual_yield", "total_yield",
      "percent_hedged", "percent_hedged_w_offer", "percent_physical", "crop_year",
    ]);

    // Map column IDs to the meta filter conditions they imply (on top of customer + commodity)
    const columnMetaFilters: Record<string, FilterNode[]> = {
      futures_hta_qty: [{ type: "condition", field: "meta.is_hta", operator: "is_true", value: "" }, { type: "condition", field: "meta.contributes_to_futures_hedging", operator: "is_true", value: "" }],
      futures_hta_pct: [{ type: "condition", field: "meta.is_hta", operator: "is_true", value: "" }, { type: "condition", field: "meta.contributes_to_futures_hedging", operator: "is_true", value: "" }],
      futures_otc_qty: [{ type: "condition", field: "meta.is_otc", operator: "is_true", value: "" }, { type: "condition", field: "meta.contributes_to_futures_hedging", operator: "is_true", value: "" }],
      futures_otc_pct: [{ type: "condition", field: "meta.is_otc", operator: "is_true", value: "" }, { type: "condition", field: "meta.contributes_to_futures_hedging", operator: "is_true", value: "" }],
      futures_brokerage_qty: [{ type: "condition", field: "meta.contributes_to_futures_hedging", operator: "is_true", value: "" }, { type: "condition", field: "row_type", operator: "eq", value: "position" }],
      futures_brokerage_pct: [{ type: "condition", field: "meta.contributes_to_futures_hedging", operator: "is_true", value: "" }, { type: "condition", field: "row_type", operator: "eq", value: "position" }],
      futures_dpq_qty: [{ type: "condition", field: "meta.is_dpq", operator: "is_true", value: "" }, { type: "condition", field: "meta.contributes_to_futures_hedging", operator: "is_true", value: "" }],
      futures_dpq_pct: [{ type: "condition", field: "meta.is_dpq", operator: "is_true", value: "" }, { type: "condition", field: "meta.contributes_to_futures_hedging", operator: "is_true", value: "" }],
      futures_open_offers_qty: [{ type: "condition", field: "meta.is_open_offer", operator: "is_true", value: "" }],
      futures_open_offers_pct: [{ type: "condition", field: "meta.is_open_offer", operator: "is_true", value: "" }],
      futures_direct_sales_qty: [{ type: "condition", field: "meta.is_direct_sale", operator: "is_true", value: "" }, { type: "condition", field: "meta.contributes_to_futures_hedging", operator: "is_true", value: "" }],
      futures_direct_sales_pct: [{ type: "condition", field: "meta.is_direct_sale", operator: "is_true", value: "" }, { type: "condition", field: "meta.contributes_to_futures_hedging", operator: "is_true", value: "" }],
      futures_unprotected_qty: [{ type: "condition", field: "meta.contributes_to_futures_hedging", operator: "is_false", value: "" }],
      futures_unprotected_pct: [{ type: "condition", field: "meta.contributes_to_futures_hedging", operator: "is_false", value: "" }],
      physical_basis_qty: [{ type: "condition", field: "meta.is_basis_only", operator: "is_true", value: "" }, { type: "condition", field: "meta.contributes_to_physical_hedging", operator: "is_true", value: "" }],
      physical_basis_pct: [{ type: "condition", field: "meta.is_basis_only", operator: "is_true", value: "" }, { type: "condition", field: "meta.contributes_to_physical_hedging", operator: "is_true", value: "" }],
      physical_otc_qty: [{ type: "condition", field: "meta.is_otc", operator: "is_true", value: "" }, { type: "condition", field: "meta.is_basis_only", operator: "is_true", value: "" }],
      physical_otc_pct: [{ type: "condition", field: "meta.is_otc", operator: "is_true", value: "" }, { type: "condition", field: "meta.is_basis_only", operator: "is_true", value: "" }],
      physical_dpq_qty: [{ type: "condition", field: "meta.is_dpq", operator: "is_true", value: "" }, { type: "condition", field: "meta.contributes_to_physical_hedging", operator: "is_true", value: "" }],
      physical_dpq_pct: [{ type: "condition", field: "meta.is_dpq", operator: "is_true", value: "" }, { type: "condition", field: "meta.contributes_to_physical_hedging", operator: "is_true", value: "" }],
      oddities_physical_qty: [{ type: "condition", field: "meta.is_direct_sale", operator: "is_true", value: "" }, { type: "condition", field: "meta.contributes_to_physical_hedging", operator: "is_true", value: "" }],
      oddities_physical_pct: [{ type: "condition", field: "meta.is_direct_sale", operator: "is_true", value: "" }, { type: "condition", field: "meta.contributes_to_physical_hedging", operator: "is_true", value: "" }],
      physical_cash_qty: [{ type: "condition", field: "meta.is_cash", operator: "is_true", value: "" }],
      physical_cash_pct: [{ type: "condition", field: "meta.is_cash", operator: "is_true", value: "" }],
      physical_unprotected_qty: [{ type: "condition", field: "meta.contributes_to_physical_hedging", operator: "is_false", value: "" }],
      physical_unprotected_pct: [{ type: "condition", field: "meta.contributes_to_physical_hedging", operator: "is_false", value: "" }],
    };

    // Always include customer
    if (row.customer_id) {
      conditions.push({
        type: "condition",
        field: "customer_id",
        operator: "eq",
        value: row.customer_id,
      });
    }

    // If it's not a customer-only column, also include commodity
    if (!customerOnlyColumns.has(columnId)) {
      conditions.push({
        type: "condition",
        field: "commodity_group",
        operator: "eq",
        value: row.commodity_group,
      });
    }

    // If the column has specific meta filters, add them
    if (columnMetaFilters[columnId]) {
      conditions.push(...columnMetaFilters[columnId]);
    }

    setRootFilter({
      type: "and",
      children: conditions,
    });
  };

  // Handle voice-generated filters
  const handleVoiceFilter = (filter: FilterNode, cropYear?: number) => {
    // Resolve fuzzy matches
    const resolvedFilter = resolveFuzzyMatches(filter, customersRaw, tradersRaw, commodityGroups);
    
    // Apply the filter
    setRootFilter(resolvedFilter);
    
    // Update crop year if specified
    if (cropYear != undefined) {
      setSelectedCropYear(cropYear);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="border rounded p-4 bg-gray-50">
        <h2 className="text-lg font-bold mb-4">Filters</h2>
        
        <div className="mb-4 flex items-center gap-2">
          <label className="text-sm font-semibold">Crop Year:</label>
          <Combobox
            options={cropYearOptions}
            value={String(selectedCropYear)}
            onValueChange={(val: string) => setSelectedCropYear(parseInt(val, 10))}
            placeholder="Select crop year..."
            className="w-32 h-7 text-xs"
            dwimOnChange
          />
{(isLoading || isFetching) ? "Loading..." : ""}
        </div>
        
        <FilterBuilder
          node={rootFilter}
          onChange={setRootFilter}
          onRemove={() => {}}
          depth={0}
          customers={customers}
          traders={traders}
          commodityGroups={commodityGroups}
        />
      </div>
      
      {!isDetailFiltered && (
        <div className="border rounded p-4">
          <h2 className="text-lg font-bold mb-4">Commodity Profiles</h2>
          <V3ProfileCards
            rows={filteredData?.rows ?? []}
            profiles={data.data.profiles}
            customerNameMap={customerNameMap}
            filteredCustomerIds={filteredCustomerIds}
            filteredCommodityGroups={filteredCommodityGroups}
          />
        </div>
      )}

      <div className="border rounded p-4">
        <h2 className="text-lg font-bold mb-4">Detailed View</h2>
        <DetailedCardsSection
          rows={filteredData?.rows ?? []}
          customerNameMap={customerNameMap}
          filteredCustomerIds={filteredCustomerIds}
        />
      </div>

      <div className="border rounded p-4">
        <V3TopLevelSummaryTable
          rows={filteredData?.rows ?? []}
          profiles={data.data.profiles}
          customerNameMap={customerNameMap}
          cropYear={selectedCropYear}
          onCellClick={handleSummaryTableCellClick}
        />
      </div>


    </div>
  );
}
